import { AccordionGroup, AccordionItem } from "@theme/accordions";
import { events, publish, subscribe } from "@theme/event-bus";
import { classes, debounce, on, updateSectionElements } from "@theme/helpers";
import { SelectProvider } from "@theme/select-provider";
import { ToggleButton, ToggleElement } from "@theme/toggle-element";
import Lenis from "@theme/lenis";

/**
 * Keyboard events.
 */
on("keyup", document, (event) => {
  if (event.key === "Escape") publish(events.escapeKey);
  if (event.key === "Tab") document.body.classList.add(classes.tabbable);
});

/**
 * AOS (Animate On Scroll). Init once; it already listens to native scroll
 * (which Lenis updates). Do not refresh on every Lenis frame — refresh() is a
 * full layout recalc and this file is an ES module, so a bare `AOS` identifier
 * would also throw. Kick once after init in case AOS missed DOMContentLoaded.
 */
if (window.AOS) {
  window.AOS.init({
    once: true,
    duration: 700,
    easing: "ease-out-cubic",
    offset: 80,
  });
  window.AOS.refresh();

  /**
   * AOS caches each element's offset at init, so layout shifts after that
   * (sliders initialising, fonts, lazy images) leave stale trigger points and
   * elements further down the page never animate in until a resize refreshes
   * them. Re-measure whenever the page height changes.
   */
  new ResizeObserver(debounce(() => window.AOS.refresh())).observe(document.body);

  on("shopify:section:load", document, () => window.AOS.refreshHard());
}

let lenis;

if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
  let lastLenisFrame = performance.now();

  // One rAF driver only. `autoRaf: true` plus a manual loop advances the lerp
  // twice per frame and desyncs native scroll. If the main thread stalls
  // (product grids, image decode), skip Lenis for that wheel so the page
  // still moves instead of feeling sticky.
  lenis = new Lenis({
    anchors: true,
    autoRaf: false,
    naiveDimensions: true,
    virtualScroll: () => performance.now() - lastLenisFrame < 80,
    prevent: (node) =>
      node instanceof HTMLElement &&
      (node.classList.contains("drawer-body") ||
        node.classList.contains("cart-upsell__grid") ||
        node.classList.contains("search-overlay__cards-scroller")),
  });

  const raf = (time) => {
    lastLenisFrame = performance.now();
    lenis.raf(time);
    requestAnimationFrame(raf);
  };
  requestAnimationFrame(raf);

  subscribe(events.sectionUpdate, () => lenis.dimensions.resize());
}

/**
 * Custom elements.
 */
customElements.define("select-provider", SelectProvider);
customElements.define("toggle-element", ToggleElement);
customElements.define("toggle-button", ToggleButton);
customElements.define("accordion-item", AccordionItem);
customElements.define("accordion-group", AccordionGroup);

/**
 * Handles Section Rendering API events.
 */
subscribe(
  events.sectionUpdate,
  async ({ sections, onAfterElementsUpdated }) => {
    await Promise.all(
      Object.entries(sections).map(([sectionId, sectionHtml]) =>
        updateSectionElements(sectionId, sectionHtml),
      ),
    );

    if (typeof onAfterElementsUpdated === "function") {
      onAfterElementsUpdated();
    }
  },
);
